import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  CheckCircle2,
  Smartphone,
  Infinity as InfinityIcon,
  Settings2,
  BookCheck,
  ArrowRight,
  ShoppingCart,
  CalendarDays,
} from "lucide-react";
import LeadForm from "@/components/LeadForm";
import OrderForm from "@/components/OrderForm";
import {
  brand,
  hero,
  intro,
  reasons,
  pillars,
  featureModules,
  featuresIntro,
  deepDive,
  lagkrav,
  services,
  mallar,
  navLinks,
  footer,
} from "@/lib/content";

const pillarIcons = [Smartphone, InfinityIcon, Settings2, BookCheck];

export default function Layout1() {
  return (
    <div className="min-h-screen bg-slate-50 font-plex text-slate-900">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-outfit text-lg tracking-tight" data-testid="l1-brand">
            Redoc <span className="text-blue-600">AML &amp; KYC</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600">
            {navLinks.map((l) => (
              <a key={l.id} href={`#${l.id}`} className="hover:text-slate-900 transition-colors">
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Button
              asChild
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              data-testid="l1-nav-cta"
            >
              <a href="#kontakt">Boka demo</a>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-slate opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              {hero.eyebrow}
            </span>
            <h1 className="font-outfit mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900">
              {hero.title}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl">
              {hero.subtitle}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-6 rounded-md text-base"
                data-testid="l1-hero-cta-demo"
              >
                <a href="#kontakt">
                  {hero.primaryCta} <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="h-12 px-6 rounded-md border-slate-300 text-base"
                data-testid="l1-hero-cta-info"
              >
                <a href="#kontakt">{hero.secondaryCta}</a>
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-600">
              {["Penningtvättlagen", "Auktoriserade redovisningskonsulter", "Servrar i Sverige"].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600" /> {t}
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white">
              <img
                src="https://customer-assets.emergentagent.com/job_order-landing-2/artifacts/v7fcvyl9_monitor-1307227_1920%20%281%29.jpg"
                alt="Redoc AML & KYC – webbaserad tjänst"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-14 text-center">
          <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
            {intro.paragraph}
          </p>
        </div>
      </section>

      {/* Reasons */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.18em] text-blue-700 mb-3">Varför Redoc</p>
          <h2 className="font-outfit text-3xl sm:text-4xl tracking-tight text-slate-900">
            Anledningar att välja Redoc AML &amp; KYC
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reasons.map((r) => (
            <div
              key={r.title}
              className="bg-white border border-slate-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-sm transition"
              data-testid={`l1-reason-${r.title}`}
            >
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <h3 className="font-outfit text-lg text-slate-900">{r.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{r.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((p, i) => {
            const Icon = pillarIcons[i] || ShieldCheck;
            return (
              <div key={p.title} className="bg-white/5 border border-white/10 rounded-xl p-6">
                <Icon className="w-6 h-6 text-blue-300" />
                <h3 className="font-outfit text-xl mt-4">{p.title}</h3>
                <p className="text-sm text-slate-300 mt-2 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Features */}
      <section id="funktioner" className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.18em] text-blue-700 mb-3">Funktioner</p>
          <h2 className="font-outfit text-3xl sm:text-4xl tracking-tight">
            Funktioner som ingår i Redoc AML &amp; KYC
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {featureModules.map((mod) => (
            <div
              key={mod.heading}
              className="bg-white border border-slate-200 rounded-2xl p-7"
              data-testid={`l1-features-${mod.heading}`}
            >
              <h3 className="font-outfit text-xl text-slate-900">{mod.heading}</h3>
              <ul className="mt-5 space-y-2.5">
                {mod.items.map((it) => (
                  <li key={it} className="flex items-start gap-2.5 text-base text-slate-900">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-10 text-base text-slate-900 leading-relaxed max-w-4xl">
          {featuresIntro}
        </p>
      </section>

      {/* Deep dive KYC / AML */}
      <section id="kyc" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.18em] text-blue-700 mb-3">KYC &amp; Riskbedömning</p>
            <h2 className="font-outfit text-3xl sm:text-4xl tracking-tight">
              Beskrivning av kundkännedom och allmän riskbedömning
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {deepDive.map((d) => (
              <div
                key={d.title}
                className="rounded-2xl border border-slate-200 p-7 bg-slate-50"
                data-testid={`l1-deep-${d.title}`}
              >
                <h3 className="font-outfit text-xl text-slate-900">{d.title}</h3>
                <p className="mt-3 text-base text-slate-900 leading-relaxed whitespace-pre-line">
                  {d.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lagkrav */}
      <section id="lagkrav" className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.18em] text-blue-700 mb-3">Lagkrav</p>
          <h2 className="font-outfit text-3xl sm:text-4xl tracking-tight">
            {lagkrav.title}
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {lagkrav.items.map((it) => (
            <div
              key={it.title}
              className="bg-white border border-slate-200 rounded-xl p-6"
            >
              <h3 className="font-outfit text-lg text-slate-900">{it.title}</h3>
              <p className="mt-2 text-base text-slate-900 leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section id="tjanster" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.18em] text-blue-700 mb-3">Tjänster</p>
            <h2 className="font-outfit text-3xl sm:text-4xl tracking-tight">
              {services.title}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.items.map((s) => (
              <div
                key={s.title}
                className="rounded-2xl border border-slate-200 p-7 hover:border-blue-300 transition"
              >
                <h3 className="font-outfit text-xl text-slate-900">{s.title}</h3>
                <p className="mt-3 text-base text-slate-900 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <h3 className="font-outfit text-2xl text-slate-900">{mallar.title}</h3>
            <div className="mt-5 grid md:grid-cols-2 gap-5">
              {mallar.items.map((m) => (
                <div key={m.title} className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                  <h4 className="font-outfit text-base text-slate-900">{m.title}</h4>
                  <p className="mt-2 text-base text-slate-900 leading-relaxed">{m.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Forms */}
      <section id="kontakt" className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order form — blue framed white card */}
          <div
            className="relative rounded-2xl p-[2px] bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 shadow-lg"
            data-testid="l1-form-order"
          >
            <div className="bg-white rounded-[14px] p-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-blue-700 mb-3">
                <ShoppingCart className="w-3.5 h-3.5" /> Beställning
              </div>
              <h3 className="font-outfit text-2xl text-slate-900">
                Beställ Redoc AML &amp; KYC
              </h3>
              <div className="mt-6">
                <OrderForm />
              </div>
            </div>
          </div>

          {/* Demo / contact form — blue card with checkbox + dropdown */}
          <div className="bg-blue-600 text-white rounded-2xl p-8 shadow-lg" data-testid="l1-form-demo">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-blue-200 mb-3">
              <CalendarDays className="w-3.5 h-3.5" /> Kontakta oss
            </div>
            <h3 className="font-outfit text-2xl">Boka demo eller begär information</h3>
            <p className="mt-2 text-sm text-blue-100">
              Välj om ni vill boka en kostnadsfri demo eller få mer information
              om en specifik tjänst – så återkommer vi inom kort.
            </p>
            <div className="mt-6">
              <LeadForm
                mode="demo"
                sourceLayout="layout-1"
                testidPrefix="l1-demo"
                showContactOptions={true}
                theme={{
                  input: "bg-white text-slate-900 border-white/30 placeholder:text-slate-400",
                  label: "text-blue-50",
                  select: "bg-white text-slate-900 border-white/30",
                  submitButton: "bg-white text-blue-700 hover:bg-blue-50 rounded-md px-6 h-11 font-medium",
                  dateButton: "bg-white text-slate-900 border-white/30 hover:bg-blue-50",
                  checkbox: "bg-white",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-8">
          <div>
            <div className="font-outfit text-lg text-white">{brand.name}</div>
            <p className="mt-3 text-sm text-slate-400 max-w-sm">{footer.description}</p>
          </div>
          <div className="text-sm">
            <p className="text-white font-medium mb-3">Snabblänkar</p>
            <ul className="space-y-2">
              {navLinks.map((l) => (
                <li key={l.id}>
                  <a href={`#${l.id}`} className="hover:text-white">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>
          <div className="text-sm">
            <p className="text-white font-medium mb-3">Kontakt</p>
            <p>Kontakta oss via kontaktformuläret ovan.</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-4 flex justify-end">
          <nav
            className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-400"
            data-testid="l1-footer-legal"
          >
            <Link to="/villkor" className="hover:text-white transition-colors" data-testid="l1-footer-terms">
              Användarvillkor
            </Link>
            <span className="text-slate-600">·</span>
            <Link to="/integritetspolicy" className="hover:text-white transition-colors" data-testid="l1-footer-privacy">
              Integritetspolicy
            </Link>
            <span className="text-slate-600">·</span>
            <Link to="/cookies" className="hover:text-white transition-colors" data-testid="l1-footer-cookies">
              Cookies
            </Link>
          </nav>
        </div>
        <div className="border-t border-white/10 py-5">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-3 items-center text-xs text-slate-500">
            <div className="text-left" data-testid="l1-footer-copy">{footer.copy}</div>
            <div className="text-center" data-testid="l1-footer-credit">
              Web by{" "}
              <a
                href="https://logartgroup.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-blue-400 transition-colors duration-200"
                data-testid="l1-footer-logardt"
              >
                Logardt Group
              </a>
            </div>
            <div className="text-right" data-testid="l1-footer-domain">amlkyc.se</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
